import { CoinBadgeV2 } from "@components/CoinBadgeV2";
import { PricesModal } from "@components/modals/PricesModal";
import { useCoins } from "@contexts/CoinsContext";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";
import useAlert from "@hooks/AlertHook";
import { errorPromise } from "@hooks/ApiHook";
import useModal from "@hooks/ModalHook";
import { useTxModal } from "@hooks/TxModalHook";
import { useWasmQuery } from "@hooks/WasmQueryHook";
import { useWasmX } from "@hooks/WasmXHook";
import { useWallet } from "@providers/wallet.provider";
import { CollectionResponse, NftResponse } from "baaanggg";
import React, { useEffect, useMemo, useState } from "react";
import NftTransferItemBox from "./NftTransferItemBox"; // <-- pastikan path sesuai

export interface Props {
  nft: NftResponse;
  collection: CollectionResponse;
  onTransferSuccess: () => void;

}

function NftSellItemBox({ nft, collection,onTransferSuccess }: Props) {
  const { openModal } = useModal();
  const wasmx = useWasmX();
  const alert = useAlert();
  const { getCoin } = useCoins();
  const { wallet } = useWallet();
  const txModal = useTxModal();

  const [prices, setPrices] = useState<{ amount: number; coinId: string }[]>([]);

  const { data: sellInfo, refresh: refreshSellInfo } = useWasmQuery(
    (wasmx) => {
      if (!collection) return errorPromise();
      return wasmx.collection.query.sellInfo({
        contract: collection.collectionContract,
        data: { token_id: nft.tokenId },
      });
    },
    [collection]
  );

  useEffect(() => {
    const pricesStr = localStorage.getItem("nftPrices");
    if (pricesStr !== null) setPrices(JSON.parse(pricesStr)[nft.id] ?? []);
  }, [nft?.id]);

  const changePrices = (p: { amount: number; coinId: string }[]) => {
    setPrices(p);
    const pricesStr = localStorage.getItem("nftPrices");
    let localPrices: { [key: string]: any } = pricesStr !== null ? JSON.parse(pricesStr) : {};
    localPrices[nft.id] = p;
    localStorage.setItem("nftPrices", JSON.stringify(localPrices));
  };

  const isListed = !!sellInfo;
  const owner = nft.ownerAddress;

  // Validasi alamat simple (cegah typo fatal). Kalau kamu punya util/SDK, ganti dengan validator resmi.
  const validateAddr = (addr?: string) => {
    if (!addr) return "Recipient address is required";
    const a = addr.trim();
    // Terra Classic typical prefix "terra", panjang kira-kira 44
    if (!a.startsWith("terra") || a.length < 40 || a.length > 64) return "Invalid Terra address format";
    if (a === owner) return "Recipient cannot be the current owner";
    return null;
  };

  const onSell = async () => {
    try {
      if (prices.length === 0) throw new Error("Please set the sell prices first");
      if (!wasmx) throw new Error("Cannot load chain information, try to refresh the page");

      const approveMsg = new MsgExecuteContract({
        contract: collection.nftContract,
        sender: owner,
        msg: {
          approve: { spender: collection.collectionContract, token_id: nft.tokenId },
        },
        funds: [],
      });

      const sellMsg = new MsgExecuteContract({
        contract: collection.collectionContract,
        sender: owner,
        msg: {
          sell: {
            prices: prices.map(({ amount, coinId }) => {
              const coin = getCoin(coinId)!;
              if (coin.isCw20) {
                return { token: { address: coin.cw20Address!, amount: amount.toString() } };
              } else {
                return { coin: { denom: coin.denom!, amount: amount.toString() } };
              }
            }),
            token_id: nft.tokenId,
          },
        },
        funds: [],
      });

      await txModal.show([approveMsg, sellMsg], `Sell NFT '${nft.name}'`, {
        onSuccess: async (tx) => {
          try {
            alert.loading("Sign Tx");
            await wallet!.pollTx(tx, { maxAttempts: 120_000, intervalSeconds: 15 });
          } catch (e) {
            console.warn("poll failed", e);
          }
          alert.success("NFT is now on sale");
          await refreshSellInfo();
        },
        onError: (er) => alert.failed(er.message),
      });
    } catch (e) {
      if (e instanceof Error) alert.failed(e.message);
      else console.error(e);
    }
  };

  const onCancelSell = async () => {
    try {
      if (!wasmx) throw new Error("Cannot load chain information, try to refresh the page");

      const revokeMsg = new MsgExecuteContract({
        contract: collection.nftContract,
        sender: owner,
        msg: { revoke: { spender: collection.collectionContract, token_id: nft.tokenId } },
        funds: [],
      });

      const cancelSellMsg = new MsgExecuteContract({
        contract: collection.collectionContract,
        sender: owner,
        msg: { cancel_sell: { token_id: nft.tokenId } },
        funds: [],
      });

      await txModal.show([revokeMsg, cancelSellMsg], `Cancel Sell NFT '${nft.name}'`, {
        onSuccess: async (tx) => {
          try {
            alert.loading("Sign Tx");
            await wallet!.pollTx(tx, { maxAttempts: 120_000, intervalSeconds: 15 });
          } catch (e) {
            console.warn("poll failed", e);
          }
          alert.success("NFT is now not for sale");
          await refreshSellInfo();
        },
        onError: (er) => alert.failed(er.message),
      });
    } catch (e) {
      if (e instanceof Error) alert.failed(e.message);
      else console.error(e);
    }
  };

  // 🔹 Handler TRANSFER
  const onTransfer = async (recipientAddress?: string) => {
    try {
      if (!wasmx) throw new Error("Cannot load chain information, try to refresh the page");

      if (isListed) {
        // Biar jelas: nggak bisa transfer ketika listing aktif
        throw new Error("NFT is currently on sale. Please cancel the sale before transferring.");
      }

      const err = validateAddr(recipientAddress);
      if (err) throw new Error(err);

      const tx = new MsgExecuteContract({
        contract: collection.nftContract, // cw721
        sender: owner,
        msg: {
          transfer_nft: {
            recipient: recipientAddress!.trim(),
            token_id: nft.tokenId,
          },
        },
        funds: [],
      });

      await txModal.show([tx], `Transfer NFT '${nft.name}'`, {
        onSuccess: async (hash) => {
          try {
            alert.loading("Sign Tx");
            await wallet!.pollTx(hash, { maxAttempts: 120_000, intervalSeconds: 15 });
          } catch (e) {
            console.warn("poll failed", e);
          }
          alert.success("NFT transferred successfully");
          await onTransferSuccess(); // minimal refresh state listing
        },
        onError: (er) => alert.failed(er.message),
      });
    } catch (e) {
      if (e instanceof Error) alert.failed(e.message);
      else console.error(e);
    }
  };

  // UI
  return (
    <div className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900">
      {/* Prices / Current listing */}
      {!sellInfo && (
        <div className="flex flex-wrap">
          {prices.map((price) => (
            <div className="mb-2.5 mr-2.5" key={price.coinId}>
              <CoinBadgeV2 amount={price.amount} coinId={price.coinId} />
            </div>
          ))}

          <button
            onClick={() => openModal("prices")}
            className="group mb-2.5 mr-2.5 inline-flex items-center rounded-lg border border-jacarta-100 bg-white px-4 py-3 hover:border-transparent hover:bg-accent hover:text-white dark:border-jacarta-600 dark:bg-jacarta-800 dark:text-white dark:hover:border-transparent dark:hover:bg-jacarta-500"
          >
            <span className="text-2xs font-medium">Edit Prices</span>
          </button>
        </div>
      )}

      {sellInfo && (
        <div className="flex flex-wrap">
          {sellInfo.prices.map((price) => {
            const coin = getCoin(price.coin?.denom ?? price.token?.address ?? "");
            return (
              <div className="mb-2.5 mr-2.5" key={coin?.id}>
                <CoinBadgeV2
                  amount={Number(price.coin?.amount ?? price.token?.amount)}
                  coinId={coin?.id!}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Sell / Cancel */}
      {!sellInfo ? (
        <button
          onClick={onSell}
          className="mb-4 inline-block w-full rounded-full bg-jacarta-500 px-8 py-3 text-center font-semibold text-white transition-all hover:bg-jacarta-600"
        >
          Sell NFT
        </button>
      ) : (
        <button
          onClick={onCancelSell}
          className="mb-4 inline-block w-full rounded-full bg-jacarta-500 px-8 py-3 text-center font-semibold text-white transition-all hover:bg-jacarta-600"
        >
          Cancel Sell
        </button>
      )}

      {/* Transfer section */}
      <div className="mt-4 border-t border-jacarta-100 pt-6 dark:border-gray-700">
        <NftTransferItemBox nft={nft} onTransfer={onTransfer} />
        {isListed && (
          <p className="mt-2 text-2xs text-red-500">
            NFT is listed for sale — cancel the sale before transferring.
          </p>
        )}
      </div>

      <PricesModal
        onChange={(p) =>
          changePrices(
            Object.entries(p).map(([coinId, amount]) => ({ coinId, amount }))
          )
        }
        prices={prices.reduce((prev, curr) => ({ ...prev, [curr.coinId]: curr.amount }), {})}
      />
    </div>
  );
}

export default NftSellItemBox;
